import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, MessageSquare, FileText, Image as ImageIcon, LogOut,
  Download, Trash2, RefreshCcw, Search, Pencil, Plus, Save, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useSeo from '../hooks/useSeo';
import RelatedPicker from '../components/admin/RelatedPicker';
import CoverUpload from '../components/admin/CoverUpload';
import QuoteArtwork from '../components/admin/QuoteArtwork';
import BlockEditor from '../components/admin/BlockEditor';
import { cleanBlocks, withKeys } from '../components/admin/blockFields';
import { api, getToken } from '../lib/api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'quotes', label: 'Quotes', icon: MessageSquare },
  { id: 'blogs', label: 'Blog Posts', icon: FileText },
  { id: 'content', label: 'Site Content', icon: ImageIcon },
];

const CONTENT_KEYS = [
  { key: 'home_bento', label: 'Home — Bento Sections' },
  { key: 'case_studies', label: 'Case Studies' },
  { key: 'brand_marquee', label: 'Brand Marquee (text list)' },
  { key: 'client_logos', label: 'Client Logos' },
];

// ============================================================
// Helpers
// ============================================================
function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

const STATUS_BADGE = {
  new: 'bg-[#00b8ff]/15 text-[#00b8ff] border-[#00b8ff]/40',
  contacted: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  archived: 'bg-white/5 text-[#cfc8bb] border-white/10',
};

// ============================================================
// Quotes Tab
// ============================================================
function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ new: 0, contacted: 0, archived: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([api.adminListQuotes(statusFilter), api.adminQuoteStats()])
      .then(([list, s]) => {
        if (cancel) return;
        setQuotes(list);
        setStats(s);
      })
      .catch((e) => {
        if (process.env.NODE_ENV !== 'production') console.error('QuotesTab: load failed', e);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [statusFilter, reloadKey]);

  const updateStatus = async (id, status) => {
    await api.adminUpdateQuoteStatus(id, status);
    setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quote request? This cannot be undone.')) return;
    await api.adminDeleteQuote(id);
    setSelected(null);
    reload();
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return quotes;
    return quotes.filter((x) =>
      [x.name, x.email, x.company, x.client_type, x.event_type, x.quantity].some(
        (v) => v && v.toLowerCase().includes(q),
      ),
    );
  }, [quotes, search]);

  const downloadCsv = async () => {
    const res = await fetch(api.exportQuotesCsvUrl(), {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'pusha-quotes.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-quotes-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total', val: stats.total, color: 'text-[#f4efe6]' },
          { label: 'New', val: stats.new, color: 'text-[#00b8ff]' },
          { label: 'Contacted', val: stats.contacted, color: 'text-emerald-300' },
          { label: 'Archived', val: stats.archived, color: 'text-[#cfc8bb]' },
        ].map((s) => (
          <div key={s.label} className="bg-[#0a0a0a] border border-white/10 rounded-xl p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#cfc8bb] mb-2">{s.label}</p>
            <p className={`font-display text-4xl ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
          <input
            data-testid="admin-quotes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, company…"
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-[#f4efe6] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#00b8ff]"
          />
        </div>
        <select
          data-testid="admin-quotes-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2.5 text-[#f4efe6] cursor-pointer"
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="archived">Archived</option>
        </select>
        <button
          onClick={reload}
          className="inline-flex items-center gap-2 border border-white/10 rounded-lg px-4 py-2.5 text-[#f4efe6] hover:border-[#00b8ff]"
        >
          <RefreshCcw className="w-4 h-4" />Refresh
        </button>
        <button
          data-testid="admin-quotes-export-csv"
          onClick={downloadCsv}
          className="inline-flex items-center gap-2 bg-[#00b8ff] text-black rounded-lg px-4 py-2.5 font-semibold hover:bg-[#f4efe6]"
        >
          <Download className="w-4 h-4" />Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-quotes-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#cfc8bb] border-b border-white/10">
                <th className="px-5 py-3">Submitted</th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Quantity</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#cfc8bb]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#cfc8bb]">No quote requests yet.</td></tr>
              ) : filtered.map((q) => (
                <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-4 text-[#cfc8bb] whitespace-nowrap">{formatDate(q.created_at)}</td>
                  <td className="px-5 py-4 text-[#f4efe6] font-medium">{q.name}</td>
                  <td className="px-5 py-4 text-[#cfc8bb]">{q.email}</td>
                  <td className="px-5 py-4 text-[#cfc8bb]">
                    {q.client_type || q.event_type}
                    {q.is_event && <span className="ml-2 text-[10px] uppercase tracking-[0.16em] text-[#00b8ff]">Live Events</span>}
                  </td>
                  <td className="px-5 py-4 text-[#cfc8bb]">{q.quantity || '—'}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block text-xs uppercase tracking-[0.16em] px-2.5 py-1 rounded border ${STATUS_BADGE[q.status] || ''}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => setSelected(q)}
                      className="text-[#00b8ff] hover:underline text-xs uppercase tracking-[0.18em]"
                      data-testid={`view-quote-${q.id}`}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <QuoteDetailModal
          quote={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(s) => updateStatus(selected.id, s)}
          onDelete={() => remove(selected.id)}
        />
      )}
    </div>
  );
}

function QuoteDetailModal({ quote, onClose, onStatusChange, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto">
      <button className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl bg-black border border-white/10 rounded-2xl p-8 my-8">
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/15 text-[#f4efe6] flex items-center justify-center hover:border-[#00b8ff]">
          <X className="w-5 h-5" />
        </button>
        <p className="text-xs uppercase tracking-[0.2em] text-[#00b8ff] mb-2">{formatDate(quote.created_at)}</p>
        <h2 className="font-display text-[#f4efe6] uppercase text-3xl md:text-4xl mb-2">{quote.name}</h2>
        <p className="text-[#cfc8bb] mb-6">{quote.email} · {quote.phone || 'no phone'}</p>

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          {[
            ['Company / Artist', quote.company],
            ['I am a', quote.client_type],
            ['Products', (quote.products || []).join(', ')],
            ['Decoration', (quote.decoration || []).join(', ')],
            ['Quantity', quote.quantity],
            ['Deadline', quote.deadline],
            ['Budget', quote.budget],
            ...(quote.is_event || !quote.client_type ? [
              ['Event Type', quote.event_type],
              ['Event Date', quote.event_date],
              ['Attendance', quote.attendance],
            ] : []),
            ...(quote.location ? [['Location', quote.location]] : []),
          ].map(([k, v]) => (
            <div key={k}>
              <p className="text-xs uppercase tracking-[0.18em] text-[#cfc8bb] mb-1">{k}</p>
              <p className="text-[#f4efe6]">{v || '—'}</p>
            </div>
          ))}
        </div>

        <QuoteArtwork files={quote.artwork} />
        {quote.details && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.18em] text-[#cfc8bb] mb-2">Details</p>
            <p className="text-[#f4efe6] whitespace-pre-line bg-[#0a0a0a] border border-white/10 rounded-lg p-4">{quote.details}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <p className="text-xs uppercase tracking-[0.18em] text-[#cfc8bb] mr-2">Set status</p>
          {['new', 'contacted', 'archived'].map((s) => (
            <button
              key={s}
              data-testid={`set-status-${s}`}
              onClick={() => onStatusChange(s)}
              className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded border transition-colors ${
                quote.status === s ? STATUS_BADGE[s] : 'border-white/10 text-[#cfc8bb] hover:border-[#00b8ff]'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            onClick={onDelete}
            data-testid="delete-quote-btn"
            className="ml-auto inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm"
          >
            <Trash2 className="w-4 h-4" />Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Blogs Tab
// ============================================================
const BLANK_BLOG = {
  slug: '',
  title: '',
  excerpt: '',
  image: '',
  image_alt: '',
  category: '',
  read_time: '',
  published_at: new Date().toISOString().slice(0, 10),
  keywords: [],
  content: [{ type: 'p', text: '' }],
  is_published: true,
  order: 0,
};

function BlogsTab() {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const run = async () => {
      try {
        const data = await api.adminListBlogs();
        if (!cancel) setBlogs(data);
      } catch (err) {
        if (!cancel && process.env.NODE_ENV !== 'production') {
          console.error('AdminBlogs: load failed', err);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [reloadKey]);

  const remove = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    await api.adminDeleteBlog(id);
    reload();
  };

  return (
    <div data-testid="admin-blogs-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[#cfc8bb] text-sm">{blogs.length} post{blogs.length === 1 ? '' : 's'}</p>
        <button
          data-testid="admin-new-blog-btn"
          onClick={() => setEditing({ ...BLANK_BLOG })}
          className="inline-flex items-center gap-2 bg-[#00b8ff] text-black rounded-lg px-4 py-2.5 font-semibold hover:bg-[#f4efe6]"
        >
          <Plus className="w-4 h-4" />New post
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? <p className="text-[#cfc8bb]">Loading…</p> : blogs.map((b) => (
          <article key={b.id} className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden flex flex-col">
            {b.image && <img src={b.image} alt={b.image_alt || b.title} className="w-full aspect-[16/9] object-cover" />}
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#00b8ff] mb-2">
                <span>{b.category || 'Uncategorized'}</span>
                <span className="w-1 h-1 rounded-full bg-[#00b8ff]/60" />
                <span className={b.is_published ? 'text-emerald-300' : 'text-amber-300'}>
                  {b.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              <h3 className="font-display text-[#f4efe6] uppercase text-xl leading-tight mb-2">{b.title}</h3>
              <p className="text-[#cfc8bb] text-sm mb-4 line-clamp-3">{b.excerpt}</p>
              <div className="mt-auto flex gap-2">
                <button
                  onClick={() => setEditing(b)}
                  data-testid={`edit-blog-${b.slug}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f4efe6] hover:border-[#00b8ff]"
                >
                  <Pencil className="w-4 h-4" />Edit
                </button>
                <button
                  onClick={() => remove(b.id)}
                  className="inline-flex items-center justify-center w-10 h-10 border border-white/10 rounded-lg text-red-400 hover:border-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {editing && (
        <BlogEditor
          blog={editing}
          others={blogs.filter((b) => b.id !== editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function BlogEditor({ blog, others, onClose, onSaved }) {
  const isNew = !blog.id;
  const [form, setForm] = useState({
    ...blog,
    related_slugs: blog.related_slugs || [],
    keywords: Array.isArray(blog.keywords) ? blog.keywords.join(', ') : '',
  });
  const [blocks, setBlocks] = useState(() => withKeys(blog.content));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const parsedContent = cleanBlocks(blocks);
    const payload = {
      slug: form.slug,
      title: form.title,
      excerpt: form.excerpt,
      image: form.image,
      image_alt: form.image_alt,
      category: form.category,
      read_time: form.read_time,
      published_at: form.published_at,
      keywords: form.keywords.split(',').map((s) => s.trim()).filter(Boolean),
      related_slugs: form.related_slugs.filter((s) => others.some((o) => o.slug === s)),
      content: parsedContent,
      is_published: !!form.is_published,
      order: Number(form.order) || 0,
    };
    setSaving(true);
    try {
      if (isNew) await api.adminCreateBlog(payload);
      else await api.adminUpdateBlog(blog.id, payload);
      onSaved();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-4 py-3 text-[#f4efe6] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#00b8ff]';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto">
      <button className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <form
        onSubmit={save}
        className="relative w-full max-w-3xl bg-black border border-white/10 rounded-2xl p-8 my-8 space-y-5"
        data-testid="admin-blog-editor"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/15 text-[#f4efe6] flex items-center justify-center hover:border-[#00b8ff]">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display text-[#f4efe6] uppercase text-3xl">
          {isNew ? 'New post' : 'Edit post'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Title *</label>
            <input required value={form.title} onChange={upd('title')} className={inputCls} data-testid="blog-title-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Slug *</label>
            <input required value={form.slug} onChange={upd('slug')} className={inputCls} placeholder="kebab-case-slug" data-testid="blog-slug-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Category</label>
            <input value={form.category} onChange={upd('category')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Read time</label>
            <input value={form.read_time} onChange={upd('read_time')} className={inputCls} placeholder="6 min read" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Published date</label>
            <input type="date" value={form.published_at} onChange={upd('published_at')} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Order</label>
            <input type="number" value={form.order} onChange={upd('order')} className={inputCls} />
          </div>
        </div>

        <CoverUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} />
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Image alt text</label>
          <input value={form.image_alt} onChange={upd('image_alt')} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Excerpt</label>
          <textarea value={form.excerpt} onChange={upd('excerpt')} rows={3} className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#00b8ff] mb-2">Keywords (comma separated)</label>
          <input value={form.keywords} onChange={upd('keywords')} className={inputCls} />
        </div>
        <RelatedPicker
          options={others}
          value={form.related_slugs}
          onChange={(v) => setForm((f) => ({ ...f, related_slugs: v }))}
        />
        <BlockEditor blocks={blocks} onChange={setBlocks} />

        <label className="inline-flex items-center gap-3 text-sm text-[#f4efe6]">
          <input
            type="checkbox"
            checked={!!form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
            className="accent-[#00b8ff] w-4 h-4"
          />
          Published (visible on site)
        </label>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="blog-save-btn"
            className="inline-flex items-center gap-2 bg-[#00b8ff] text-black rounded-full px-6 py-3 font-semibold hover:bg-[#f4efe6] disabled:opacity-60"
          >
            <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save post'}
          </button>
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#cfc8bb] hover:text-[#f4efe6]">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Content Tab (JSON editor for bento, case studies, logos)
// ============================================================
function ContentTab() {
  const [activeKey, setActiveKey] = useState(CONTENT_KEYS[0].key);
  const [text, setText] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancel = false;
    setLoading(true); setError(''); setSuccess('');
    const run = async () => {
      try {
        const res = await api.adminGetContent(activeKey);
        if (cancel) return;
        setText(JSON.stringify(res.data ?? [], null, 2));
        setUpdatedAt(res.updated_at || '');
      } catch (err) {
        if (!cancel) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('AdminContent: load failed', err);
          }
          setError('Could not load this content block. Try again.');
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [activeKey, reloadKey]);

  const save = async () => {
    setError(''); setSuccess('');
    let parsed;
    try { parsed = JSON.parse(text); } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
      return;
    }
    setSaving(true);
    try {
      await api.adminPutContent(activeKey, parsed);
      setSuccess('Saved. Public site will reflect changes on next page load.');
      reload();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {CONTENT_KEYS.map((k) => (
          <button
            key={k.key}
            onClick={() => setActiveKey(k.key)}
            data-testid={`content-tab-${k.key}`}
            className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full border transition-colors ${
              activeKey === k.key
                ? 'bg-[#00b8ff] text-black border-[#00b8ff]'
                : 'border-white/10 text-[#cfc8bb] hover:border-[#00b8ff]'
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#cfc8bb]">
            Last updated: {updatedAt ? formatDate(updatedAt) : '—'}
          </p>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="content-save-btn"
            className="inline-flex items-center gap-2 bg-[#00b8ff] text-black rounded-lg px-4 py-2 font-semibold hover:bg-[#f4efe6] disabled:opacity-60"
          >
            <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={24}
          spellCheck={false}
          data-testid="content-json-textarea"
          className="w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-4 py-3 text-[#f4efe6] font-mono text-xs resize-y focus:outline-none focus:border-[#00b8ff]"
        />
        {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
        {success && <p className="text-sm text-emerald-400 mt-3">{success}</p>}
      </div>
      <div className="mt-4 text-xs text-[#7a7a7a] leading-relaxed">
        <p><strong className="text-[#cfc8bb]">Tip:</strong> Edit JSON carefully. For <code>home_bento</code> &amp; <code>case_studies</code> each entry supports a <code>media</code> array (image/video) which becomes a slideshow on the live site. <code>brand_marquee</code> is a list of strings. In <code>case_studies</code>, set <code>method</code> to <code>"Production"</code> or <code>"Live Printing"</code> — it drives the filter on /work and which projects show on /live-events. Add an optional <code>link</code> (e.g. <code>"/work/rhyme-fest"</code>) to make a project card open its case study page.</p>
      </div>
    </div>
  );
}

// ============================================================
// Overview
// ============================================================
function OverviewTab() {
  const [stats, setStats] = useState({ new: 0, contacted: 0, archived: 0, total: 0 });
  const [blogCount, setBlogCount] = useState(0);
  useEffect(() => {
    let cancel = false;
    api.adminQuoteStats()
      .then((s) => { if (!cancel) setStats(s); })
      .catch((err) => {
        if (!cancel && process.env.NODE_ENV !== 'production') {
          console.warn('OverviewTab: stats load failed', err);
        }
      });
    api.adminListBlogs()
      .then((b) => { if (!cancel) setBlogCount(b.length); })
      .catch((err) => {
        if (!cancel && process.env.NODE_ENV !== 'production') {
          console.warn('OverviewTab: blog list failed', err);
        }
      });
    return () => { cancel = true; };
  }, []);
  const cards = [
    { label: 'Total quotes', val: stats.total, accent: 'text-[#00b8ff]' },
    { label: 'New leads', val: stats.new, accent: 'text-[#00b8ff]' },
    { label: 'Contacted', val: stats.contacted, accent: 'text-emerald-300' },
    { label: 'Published posts', val: blogCount, accent: 'text-[#f4efe6]' },
  ];
  return (
    <div data-testid="admin-overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#0a0a0a] border border-white/10 rounded-xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#cfc8bb] mb-3">{c.label}</p>
            <p className={`font-display text-5xl ${c.accent}`}>{c.val}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 bg-[#0a0a0a] border border-white/10 rounded-xl p-8">
        <h3 className="font-display text-[#f4efe6] uppercase text-2xl mb-3">Quick links</h3>
        <ul className="text-sm text-[#cfc8bb] space-y-2">
          <li>• <strong className="text-[#f4efe6]">Quotes</strong> — review submissions from the Get a Quote modal, mark as contacted, or export to CSV.</li>
          <li>• <strong className="text-[#f4efe6]">Blog posts</strong> — create, edit and unpublish SEO articles shown on the home page.</li>
          <li>• <strong className="text-[#f4efe6]">Site content</strong> — edit case studies, bento sections, brand marquee and client logos as raw JSON.</li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================
// Dashboard shell
// ============================================================
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  useSeo({ title: 'Admin Dashboard | Pusha Teez', isPrivate: true });
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');

  const onLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-black text-[#f4efe6] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#050505] p-6">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="https://customer-assets.emergentagent.com/job_pusha-rebrand/artifacts/5rw0r3ms_PTZ-LOGO-BLK.png" alt="Pusha Teez" className="w-10 h-10 invert" />
          <div>
            <p className="font-display text-xl leading-none">PUSHA</p>
            <p className="font-script text-[#00b8ff] text-sm leading-none">admin</p>
          </div>
        </Link>
        <nav className="flex-1 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active === t.id ? 'bg-[#00b8ff]/15 text-[#00b8ff]' : 'text-[#cfc8bb] hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-8 pt-6 border-t border-white/10">
          <p className="text-xs text-[#cfc8bb] mb-3">{user && user.email}</p>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-sm text-[#cfc8bb] hover:text-[#f4efe6]"
          >
            <LogOut className="w-4 h-4" />Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#050505] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="font-display text-[#f4efe6] text-lg">PUSHA <span className="font-script text-[#00b8ff] text-sm">admin</span></Link>
        <button onClick={onLogout} className="text-[#cfc8bb] text-xs uppercase tracking-[0.18em]">Log out</button>
      </div>

      <main className="flex-1 p-6 md:p-10 pt-20 md:pt-10">
        {/* Mobile tab pills */}
        <div className="md:hidden flex gap-2 overflow-x-auto pb-4 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full border whitespace-nowrap ${
                active === t.id ? 'bg-[#00b8ff] text-black border-[#00b8ff]' : 'border-white/10 text-[#cfc8bb]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mb-8">
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">
            {TABS.find((t) => t.id === active)?.label}
          </h1>
        </div>

        {active === 'overview' && <OverviewTab />}
        {active === 'quotes' && <QuotesTab />}
        {active === 'blogs' && <BlogsTab />}
        {active === 'content' && <ContentTab />}
      </main>
    </div>
  );
}
